/**
 * Datadog Browser RUM initialization.
 *
 * Provides automatic observability for the SPA:
 *  - Page load performance (Core Web Vitals)
 *  - JavaScript errors and network failures
 *  - Resource timing (API calls, fonts, images)
 *  - User interactions (clicks, form submissions)
 *  - Session replay (configurable sampling)
 *
 * **PII/PHI Protection (CRITICAL for healthcare):**
 *  - defaultPrivacyLevel: 'mask' masks ALL text content + form inputs
 *  - Only anonymized user IDs allowed (no names, emails, PHI)
 *  - Use data-dd-privacy attributes for additional fine-grained control
 *  - Session replay captures DOM structure, not just form values
 *
 * **Integration with Custom Events:**
 *  - This RUM SDK works alongside the existing observability.ts system
 *  - Custom business events (qn.submit.succeeded, etc.) continue flowing through backend
 *  - RUM SDK handles automatic technical metrics (performance, errors)
 */

import { datadogRum } from '@datadog/browser-rum';
import { appConfig } from '../config/app-config';

// Track initialization state - cannot rely on datadogRum object itself (always truthy)
let isInitialized = false;

/**
 * Initialize Datadog RUM if credentials are provided.
 * Call this once at app startup (main.tsx).
 *
 * If credentials are missing (local dev without RUM), initialization is skipped silently.
 */
export function initializeDatadogRum(): void {
  // All Datadog config resolved via appConfig (see src/shared/config/app-config.ts).
  // In deployed envs values come from window.ENV_CONFIG (runtime-injected by docker-entrypoint.sh).
  // In local dev set VITE_DATADOG_RUM_APP_ID / VITE_DATADOG_RUM_CLIENT_TOKEN in .env.local.
  const applicationId = appConfig.datadog.rumAppId;
  const clientToken = appConfig.datadog.rumClientToken;
  const environment = appConfig.environment;
  const remoteConfigId = appConfig.datadog.remoteConfigId;

  // API base URL for distributed tracing (RUM→APM correlation)
  const apiBaseUrl = appConfig.apiBaseUrl;

  // Skip initialization if credentials not provided (e.g., local dev)
  if (!applicationId || !clientToken) {
    if (import.meta.env.DEV) {
      console.info(
        '[RUM] Datadog RUM disabled: missing VITE_DATADOG_RUM_APP_ID or VITE_DATADOG_RUM_CLIENT_TOKEN',
      );
    }
    return;
  }

  try {
    // Sampling rates already parsed and validated in appConfig
    const sessionSampleRate = appConfig.datadog.sessionSampleRate;
    const sessionReplaySampleRate = appConfig.datadog.replaySampleRate;

    // Validate API base URL for distributed tracing
    // Warn if relative path or missing - tracing headers won't be injected
    if (apiBaseUrl && !apiBaseUrl.startsWith('http://') && !apiBaseUrl.startsWith('https://')) {
      if (import.meta.env.DEV) {
        console.warn(
          `[RUM] VITE_API_BASE_URL="${apiBaseUrl}" is not an absolute URL. ` +
            `Distributed tracing (RUM→APM correlation) will be disabled. ` +
            `Expected format: https://domain.com/path`,
        );
      }
    }

    datadogRum.init({
      applicationId,
      clientToken,
      site: 'datadoghq.com',
      service: 'optumus-mp-web',
      env: environment,
      version: appConfig.version,

      // Remote Configuration (optional) - allows changing RUM config without redeploying
      // Enables dynamic updates to sampling rates, privacy settings, feature toggles from Datadog UI
      remoteConfigurationId: remoteConfigId,

      // Session sampling (configurable via environment variables)
      sessionSampleRate, // Default: 100% of sessions
      sessionReplaySampleRate, // Default: 20% of sessions recorded

      // Tracking configuration
      trackUserInteractions: true, // Track clicks, form submissions
      trackResources: true, // Track API calls, fonts, images
      trackLongTasks: true, // Track JS blocking tasks >50ms

      // PII/PHI Protection (CRITICAL for healthcare data)
      // 'mask' masks ALL text content (not just inputs), safer for PHI
      // Use data-dd-privacy="allow" on specific non-sensitive elements if needed
      defaultPrivacyLevel: 'mask',

      // Allowed tracing origins (for distributed tracing with backend)
      // Only add if VITE_API_BASE_URL is a valid absolute URL (must start with http:// or https://)
      // Relative paths (e.g., "/api") will not work - allowedTracingUrls uses prefix string matching
      // against request URLs, which are always absolute (e.g., "https://test-healthsafe.uhg.com/api")
      ...(apiBaseUrl && (apiBaseUrl.startsWith('http://') || apiBaseUrl.startsWith('https://'))
        ? {
            allowedTracingUrls: [{ match: apiBaseUrl, propagatorTypes: ['tracecontext'] }],
          }
        : {}),

      // Proxy configuration (optional - if you need to route RUM data through your backend)
      // Uncomment if implementing backend RUM proxy:
      // proxy: '/v1/internal/datadog-rum-proxy',
    });

    // Note: In @datadog/browser-rum v7.x, session replay recording starts automatically
    // based on sessionReplaySampleRate. startSessionReplayRecording() is deprecated.
    // If using bundled replay or separate recorder package, manual start may be required.

    isInitialized = true;

    if (import.meta.env.DEV) {
      console.info('[RUM] Datadog RUM initialized successfully', {
        service: 'optumus-mp-web',
        env: environment,
        sessionSampleRate,
        sessionReplaySampleRate,
        privacyLevel: 'mask',
      });
    }
  } catch (error) {
    // RUM initialization failure should not crash the app
    console.error('[RUM] Failed to initialize Datadog RUM:', error);
    isInitialized = false;
  }
}

/**
 * Add custom RUM context that persists across page views.
 *
 * **CRITICAL:** Only pass anonymized, non-identifying tokens. Do NOT pass:
 *  - Real names (PHI)
 *  - Email addresses (PII)
 *  - Member IDs that directly identify patients
 *  - Any other PHI/PII
 *
 * Use hashed/tokenized IDs only (e.g., session tokens, anonymized correlation IDs).
 *
 * Example: setRumUser({ id: 'session_abc123' })
 */
export function setRumUser(user: { id?: string }): void {
  if (!isInitialized) {
    if (import.meta.env.DEV) {
      console.warn('[RUM] setRumUser called before initialization');
    }
    return;
  }
  datadogRum.setUser(user);
}

/**
 * Clear RUM user context (e.g., on logout).
 */
export function clearRumUser(): void {
  if (!isInitialized) {
    return;
  }
  datadogRum.clearUser();
}

/**
 * Add global context to all RUM events.
 * Use sparingly - prefer event-specific context via custom actions.
 *
 * **WARNING:** Do not add PHI/PII to global context.
 */
export function setRumGlobalContext(key: string, value: unknown): void {
  if (!isInitialized) {
    if (import.meta.env.DEV) {
      console.warn('[RUM] setRumGlobalContext called before initialization');
    }
    return;
  }
  datadogRum.setGlobalContextProperty(key, value);
}

/**
 * Remove global context property.
 */
export function removeRumGlobalContext(key: string): void {
  if (!isInitialized) {
    return;
  }
  datadogRum.removeGlobalContextProperty(key);
}
